import Axios, { AxiosError, CancelToken, CancelTokenSource } from 'axios'
import useSWR, { cache, mutate } from 'swr'
import { useEffect, useState } from 'react'
import { strings } from './strings'
import { store } from './store/store'
import { signedOut } from 'store/slices/auth-slice'
import _ from 'lodash'
import { JSEncrypt } from 'jsencrypt'
import { differenceInMilliseconds } from 'date-fns'
import { getLatLngFromNmea } from 'hooks'

// Axios.defaults.baseURL = 'http://46.209.216.126:8989/api'
// Axios.defaults.baseURL = 'http://81.28.61.12/api'
// Axios.defaults.baseURL = 'http://localhost/api'
Axios.defaults.baseURL = `${window.location.origin}/api`

Axios.interceptors.response.use(
  (response) => response,
  function (err: AxiosError) {
    if (err.response && err.response.status === 401) {
      store.dispatch(signedOut())
    }

    return Promise.reject(err)
  },
)

export function useServerTimeOffset(): [number, Date, Date] | null {
  const [state, setState] = useState<[number, Date, Date] | null>(null)
  useEffect(() => {
    axiosFetcher('/time', {}).then((data: any) => {
      const serverDate = new Date(data.time)
      const clientDate = new Date()
      const offset = Math.abs(differenceInMilliseconds(clientDate, serverDate))
      setState([offset, serverDate, clientDate])
    })
  }, [])
  return state
}

export function useUserStat(userId: number): ApiResponse<'userStat', UserStat> {
  const { data, error } = useSWR(
    `/user-stat?id=${userId}`,
    userId > 0 ? axiosFetcher : () => {},
  )
  return {
    userStat: data as UserStat,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/user-stat?id=${userId}`),
  }
}

export function useMountpointStat(
  mountpointId: number,
): ApiResponse<'mountpointStat', MountpointStat> {
  const { data, error } = useSWR(
    `/mountpoint-stat?id=${mountpointId}`,
    axiosFetcher,
  )
  return {
    mountpointStat: data as MountpointStat,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/mountpoint-stat?id=${mountpointId}`),
  }
}

export function useServerStat(
  mountpointName?: string | null,
): ApiResponse<'serverStat', ServerStat> {
  const KEY = mountpointName
    ? `/server-stat?mountpoint=${mountpointName}`
    : '/server-stat'

  const { data, error } = useSWR(
    mountpointName === null ? null : KEY,
    axiosFetcher,
    {
      revalidateOnFocus: false,
      // dedupingInterval: 120_000,
      loadingTimeout: 20000,
    },
  )
  return {
    serverStat: data as ServerStat,
    loading: !error && !data,
    error,
    refresh: () => mutate(KEY),
  }
}

export function useMountpointsMap() {
  const { data: allMountpoints, error } = useSWR(
    `/mountpoints-map`,
    axiosFetcher,
    {
      refreshInterval: 5000,
    },
  )

  const connectedMountpoints: MountpointMap[] = []
  const unstableMountpoints: MountpointMap[] = []
  const disconnectedMountpoints: MountpointMap[] = []
  const inactiveMountpoints: MountpointMap[] = []
  const coordAlertMountpoints: MountpointMap[] = []

  if (allMountpoints)
    for (let mountpoint of allMountpoints as MountpointMap[]) {
      if (mountpoint.coord_alert) coordAlertMountpoints.push(mountpoint)
      else if (mountpoint.inactive) inactiveMountpoints.push(mountpoint)
      else if (!mountpoint.connected) disconnectedMountpoints.push(mountpoint)
      else if (mountpoint.unstable) unstableMountpoints.push(mountpoint)
      else connectedMountpoints.push(mountpoint)
    }

  return {
    // allMountpoints: allMountpoints as MountpointMap[],
    connectedMountpoints,
    unstableMountpoints,
    disconnectedMountpoints,
    inactiveMountpoints,
    coordAlertMountpoints,
    loading: !error && !allMountpoints,
    error,
    refresh: () => mutate(`/mountpoints-map`),
  }
}

export enum FixQuality {
  NotFixed = 0,
  DGNSS = 2,
  RTK_Fixed = 4,
  RTK_Float = 5,
}
export function useClientsMap() {
  const { data, error } = useSWR(`/clients-map`, axiosFetcher, {
    refreshInterval: 5000,
  })

  // const allClients: Client[] = []
  const notFixedClients: Client[] = []
  const dgnssClients: Client[] = []
  const rtkFixedClients: Client[] = []
  const rtkFloatClients: Client[] = []

  if (data)
    for (let client of data as Client[]) {
      const nmeaData = client.lastNmea.split(',')
      const coordinates = getLatLngFromNmea(nmeaData)
      if (coordinates) {
        let temp = {
          ...client,
          latitude: coordinates[0],
          longitude: coordinates[1],
        }

        switch (parseInt(nmeaData[6])) {
          case FixQuality.DGNSS:
            temp.fixQuality = FixQuality.DGNSS
            dgnssClients.push(temp)
            break

          case FixQuality.RTK_Fixed:
            temp.fixQuality = FixQuality.RTK_Fixed
            rtkFixedClients.push(temp)
            break

          case FixQuality.RTK_Float:
            temp.fixQuality = FixQuality.RTK_Float
            rtkFloatClients.push(temp)
            break

          default:
            temp.fixQuality = FixQuality.NotFixed
            notFixedClients.push(temp)
            break
        }
      }
    }

  // const clients = (data as Client[])?.reduce<Client[]>((temp, client) => {
  //   if (client.lastNmea) {
  //     const coordinates = getLatLngFromNmea(client.lastNmea)
  //     if (coordinates) temp.push({ ...client, latitude: coordinates[0], longitude: coordinates[1] })
  //   }

  //   return temp
  // }, [])

  return {
    notFixedClients,
    dgnssClients,
    rtkFixedClients,
    rtkFloatClients,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/clients-map`),
  }
}

export function useMountpointInfo(
  mountpointId: number | undefined,
): ApiResponse<'mountpointInfo', MountpointInfo> {
  const { data, error } = useSWR(
    mountpointId !== undefined ? `/mountpoint-info?id=${mountpointId}` : null,
    axiosFetcher,
  )
  return {
    mountpointInfo: data as MountpointInfo,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/mountpoint-info?id=${mountpointId}`),
  }
}

export function useUserProfile(
  userId: number | undefined,
): ApiResponse<'userProfile', UserProfile> {
  const { data, error } = useSWR(
    userId !== undefined ? `/user-profile?id=${userId}` : null,
    axiosFetcher,
  )
  return {
    userProfile: data as UserProfile,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/user-profile?id=${userId}`),
  }
}

export function useUpdateUserProfile() {
  const cancelToken = useCancelToken()
  return (userProfile: UserProfile) => {
    return axiosPost<string>(
      cancelToken,
      '/update-user-profile',
      userProfile,
      `/user-profile?id=${userProfile.id}`,
    )
  }
}

export function useNewMountpoint() {
  const cancelToken = useCancelToken()
  return (name: string, groupId: number | null) => {
    return axiosPost<string>(
      cancelToken,
      '/new-mountpoint',
      { name, sourceType: 'server', groupIds: groupId ? [groupId] : [] },
      `/mountpoints?q=`,
    )
  }
}

export function useNewUser() {
  const cancelToken = useCancelToken()
  return (username: string, groupId: number | null) => {
    return axiosPost<string>(
      cancelToken,
      '/new-user',
      { username, groupId },
      `/users?q=`,
    )
  }
}

export function useUpdateMountpointInfo() {
  const cancelToken = useCancelToken()
  return async (mountpointInfo: MountpointInfo) => {
    let payload: any = mountpointInfo
    if (payload.serverPassword.length > 0) {
      const publicKey = (await axiosPost(
        cancelToken,
        '/auth/newkey',
        '',
      )) as string
      const encrypt = new JSEncrypt()
      encrypt.setPublicKey(publicKey)
      const encrypted = encrypt.encrypt(payload.serverPassword) || ''
      payload.serverPassword = encrypted
      payload.sourceExternalPassword = ''
      payload.publicKey = publicKey
    } else if (payload.sourceExternalPassword.length > 0) {
      const publicKey = (await axiosPost(
        cancelToken,
        '/auth/newkey',
        '',
      )) as string
      const encrypt = new JSEncrypt()
      encrypt.setPublicKey(publicKey)
      const encrypted = encrypt.encrypt(payload.sourceExternalPassword) || ''
      payload.sourceExternalPassword = encrypted
      payload.publicKey = publicKey
    }
    return axiosPost<string>(
      cancelToken,
      '/update-mountpoint-info',
      payload,
      `/mountpoint-info?id=${mountpointInfo.id}`,
    )
  }
}

export function useUserGroupsList(): ApiResponse<
  'userGroupsList',
  Array<UserGroupItem>
> {
  const { data, error } = useSWR('/user-groups', axiosFetcher)
  return {
    userGroupsList: data as Array<UserGroupItem>,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/user-groups`),
  }
}

export function useNewUserGroup() {
  const cancelToken = useCancelToken()
  return (groupName: string) => {
    return axiosPost<number>(cancelToken, '/new-user-group', {
      name: groupName,
    })
  }
}

export function useNewMountpointGroup() {
  const cancelToken = useCancelToken()
  return (groupName: string) => {
    return axiosPost<number>(cancelToken, '/new-mountpoint-group', {
      name: groupName,
    })
  }
}

export function useDeleteUserGroup() {
  const cancelToken = useCancelToken()
  return (id: number) => {
    return axiosPost<number>(cancelToken, '/delete-user-group', { id }, [
      '/users?q',
      '/user-groups',
    ])
  }
}

export function useDeleteMountpointGroup() {
  const cancelToken = useCancelToken()
  return (id: number) => {
    return axiosPost<number>(cancelToken, '/delete-mountpoint-group', { id }, [
      '/mountpoints?q',
      '/mountpoint-groups',
    ])
  }
}

export function useMountpointGroupsList(): ApiResponse<
  'mountpointGroupsList',
  Array<MountpointGroupItem>
> {
  const { data, error } = useSWR('/mountpoint-groups', axiosFetcher)
  return {
    mountpointGroupsList: data as Array<MountpointGroupItem>,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/mountpoint-groups`),
  }
}

export function useUsersList(
  query: string,
  groupId: number | null,
): ApiResponse<'usersList', Array<UserItem>> {
  let g = groupId ? `&g=${groupId}` : ''
  const { data, error } = useSWR(`/users?q=${query}${g}`, axiosFetcher)
  return {
    usersList: data as Array<UserItem>,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/users?q=${query}`),
  }
}

export function useMountpointsList(
  query: string,
  groupId: number | null,
): ApiResponse<'mountpointsList', Array<MountpointItem>> {
  let g = groupId ? `&g=${groupId}` : ''
  const { data, error } = useSWR(`/mountpoints?q=${query}${g}`, axiosFetcher)
  return {
    mountpointsList: data as Array<MountpointItem>,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/mountpoints?q=${query}`),
  }
}

export function usePermissionsList(): ApiResponse<
  'permissionsList',
  Array<PermissionItem>
> {
  const { data, error } = useSWR('/permissions', axiosFetcher)
  return {
    permissionsList: data as Array<PermissionItem>,
    loading: !error && !data,
    error,
    refresh: () => mutate(`/permissions`),
  }
}

export function useNewPermission() {
  const cancelToken = useCancelToken()
  return ({ userGroupId, mountpointGroupId }: Partial<PermissionItem>) => {
    return axiosPost<string>(
      cancelToken,
      '/new-permission',
      { userGroupId, mountpointGroupId },
      '/permissions',
    )
  }
}

export function useDeletePermission() {
  const cancelToken = useCancelToken()
  return ({ userGroupId, mountpointGroupId }: Partial<PermissionItem>) => {
    return axiosPost<string>(
      cancelToken,
      '/delete-permission',
      { userGroupId, mountpointGroupId },
      '/permissions',
    )
  }
}

export function useSignin() {
  const cancelToken = useCancelToken()
  return async (signinInfo: SigninInfo, captchaInfo: CaptchaInfo) => {
    const publicKey = (await axiosPost(
      cancelToken,
      '/auth/newkey',
      '',
    )) as string
    const encrypt = new JSEncrypt()
    encrypt.setPublicKey(publicKey)
    const encrypted = encrypt.encrypt(signinInfo.password) || ''
    const payload = {
      username: signinInfo.username,
      password: encrypted,
      publicKey: publicKey,
    }
    return axiosPost<string>(cancelToken, '/auth/signin', payload, undefined, {
      'Captcha-Key': captchaInfo.key,
      'Captcha-Value': captchaInfo.value,
    })
    // const payload = { ...signinInfo }
    // return axiosPost<string>(cancelToken, '/auth/signin', payload, undefined, {
    //   'Captcha-Key': captchaInfo.key,
    //   'Captcha-Value': captchaInfo.value,
    // })
  }
}

export function useSignup() {
  const cancelToken = useCancelToken()
  return async (signupInfo: SignupInfo, captchaInfo: CaptchaInfo) => {
    const publicKey = (await axiosPost(
      cancelToken,
      '/auth/newkey',
      '',
    )) as string
    const encrypt = new JSEncrypt()
    encrypt.setPublicKey(publicKey)
    const encrypted = encrypt.encrypt(signupInfo.password) || ''
    const payload = { ...signupInfo, password: encrypted, publicKey }
    return axiosPost<string>(cancelToken, '/auth/signup', payload, undefined, {
      'Captcha-Key': captchaInfo.key,
      'Captcha-Value': captchaInfo.value,
    })
  }
}

export function useCaptchaApi(): ApiResponse<'captchaKey', string> {
  const { data, error } = useSWR('/captcha', axiosFetcher, {
    revalidateOnFocus: false,
  })
  return {
    captchaKey: data as string,
    loading: !error && !data,
    error,
    refresh: () => mutate('/captcha'),
  }
}

export function useSettingsInfo(): ApiResponse<'settingsInfo', SettingsInfo> {
  const { data, error } = useSWR('/admin-settings', axiosFetcher)
  return {
    settingsInfo: data as SettingsInfo,
    loading: !error && !data,
    error,
    refresh: () => mutate('/settings'),
  }
}

export function useUpdateSettings() {
  const cancelToken = useCancelToken()
  return (settingsInfo: SettingsInfo) => {
    return axiosPost<string>(
      cancelToken,
      '/update-admin-settings',
      settingsInfo,
      '/admin-settings',
    )
  }
}

export function useNewPhone() {
  const cancelToken = useCancelToken()
  return (phoneNumber: string, captchaInfo: CaptchaInfo) => {
    return axiosPost<string>(
      cancelToken,
      '/otp/new-phone',
      { phoneNumber },
      undefined,
      {
        'Captcha-Key': captchaInfo.key,
        'Captcha-Value': captchaInfo.value,
      },
    )
  }
}

export function useVerifyPhone() {
  const cancelToken = useCancelToken()
  return (code: string, userId: number) => {
    return axiosPost<string>(
      cancelToken,
      '/otp/verify-phone',
      { code },
      `/user-profile?id=${userId}`,
    )
  }
}

export function useTrashUserProfile() {
  const cancelToken = useCancelToken()
  return (userId: number) => {
    return axiosPost<string>(
      cancelToken,
      '/trash-user',
      { id: userId },
      '/users?q=',
    )
  }
}

export function useTrashMountpointInfo() {
  const cancelToken = useCancelToken()
  return (mountpointId: number) => {
    return axiosPost<string>(
      cancelToken,
      '/trash-mountpoint',
      { id: mountpointId },
      '/mountpoints?q=',
    )
  }
}

export function useChangePassword() {
  const cancelToken = useCancelToken()
  return async (data: PasswordInfo, captchaInfo: CaptchaInfo) => {
    let publicKeyOld = ''
    if (data.oldPassword) {
      publicKeyOld = (await axiosPost(
        cancelToken,
        '/auth/newkey',
        '',
      )) as string
    }
    const publicKeyNew = (await axiosPost(
      cancelToken,
      '/auth/newkey',
      '',
    )) as string
    const encrypt = new JSEncrypt()
    encrypt.setPublicKey(publicKeyOld)
    const oldEncrypted = encrypt.encrypt(data.oldPassword || '') || ''
    encrypt.setPublicKey(publicKeyNew)
    const newEncrypted = encrypt.encrypt(data.newPassword) || ''
    const payload = {
      ...data,
      oldPassword: oldEncrypted,
      newPassword: newEncrypted,
      publicKeyNew,
      publicKeyOld,
    }
    return axiosPost<string>(
      cancelToken,
      '/auth/change-password',
      payload,
      undefined,
      {
        'Captcha-Key': captchaInfo.key,
        'Captcha-Value': captchaInfo.value,
      },
    )
  }
}

export function useForgotPassword() {
  const cancelToken = useCancelToken()
  return (data: ForgotInfo, captchaInfo: CaptchaInfo) => {
    return axiosPost<string>(
      cancelToken,
      '/auth/forgot-password',
      data,
      undefined,
      {
        'Captcha-Key': captchaInfo.key,
        'Captcha-Value': captchaInfo.value,
      },
    )
  }
}

export function useQueryLogs() {
  const cancelToken = useCancelToken()
  return (after: number, count: number, filter: string) => {
    return axiosPost<string>(cancelToken, '/logs', {
      After: after,
      Count: count,
      Filter: filter,
    })
  }
}

export function useCancelToken(): CancelToken {
  const [cancelToken] = useState<CancelTokenSource>(() =>
    Axios.CancelToken.source(),
  )
  useEffect(() => cancelToken.cancel, [cancelToken])
  return cancelToken.token
}

type ApiResponse<ResponseKey extends string, ResponstType> = {
  loading: boolean
  error?: AxiosError
  refresh: () => void
} & { [key in ResponseKey]?: ResponstType }

export interface UserProfile {
  id: number
  username: string
  role: string
  fullname: string
  nationalCode: string
  phoneNumber: string
  email: string
  legalEntity: string
  state: string
  city: string
  groupId: number
  groupName: string
  phoneVerified: boolean
  emailVerified: boolean
}

export interface MountpointInfo {
  id: number
  name: string
  identifier: string
  format: string
  formatDetails: string
  carrier: string
  navigationSystem: string
  network: string
  country: string
  latitude: number
  longitude: number
  altitude: number
  rtcm_latitude: number
  rtcm_longitude: number
  rtcm_altitude: number
  nmea: string
  solution: string
  generator: string
  compressionEncryption: string
  authentication: string
  fee: string
  bitRate: string
  miscellaneous: string
  sourceType: 'server' | 'nearest' | 'external' | 'proxy'
  sourceNearestGroupIds: Array<number> | null
  sourceExternalAddr: string
  sourceExternalUsername: string
  sourceExternalPassword: string
  sourceExternalMountpoint: string
  groupIds: Array<number> | null
  groupName: string
  inactive: boolean
  coord_alert: boolean
}

export interface GroupItem {
  id: number
  name: string
}

export interface UserGroupItem extends GroupItem {
  permissions: Array<GroupItem>
}

export interface MountpointGroupItem extends GroupItem {}

export interface UserItem {
  id: number
  username: string
  fullname: string
  groupName: string | null
  nationalCode: string
  connected: boolean
}

export interface MountpointItem {
  id: number
  name: string
  connectedSince: number
}

export interface PermissionItem {
  userGroupId: number
  userGroupName: string
  mountpointGroupId: number
  mountpointGroupName: string
}

export interface Client {
  userId: number
  mountpoint: string
  mountpointId: number
  sourceId: number
  lastNmea: string
  latitude: number
  longitude: number
  fixQuality: FixQuality
}

export interface MountpointMap {
  id: number
  name: string
  latitude: number
  longitude: number
  connected: boolean
  unstable: boolean
  inactive: boolean
  coord_alert: boolean
}

/**[startDate(in seconds), endDate(in seconds)] */
export type EventLog = [number, number]
export interface MountpointStat {
  id: number
  since: number
  remote: string
  sourceAgent: string
  speed: string
  dayLog: number[][] | null
  hourlyLog: number[][] | null
  eventLog: EventLog[] | null
}

export enum MountpointStatus {
  Online = 1,
  Offline,
}
export interface ServerStat {
  mountpoints: {
    [key: string]: {
      eventLog: string
      id: number
    }
  }
}

export interface UserStat {
  id: number
  mountpoint: string
  rtcmCount: number
  connectedSince: number
  remote: string
  userAgent: string
  lastNmea: string
}

export interface SigninInfo {
  username: string
  password: string
}

export interface SignupInfo {
  username: string
  password: string
  fullname: string
  nationalCode: string
  phoneNumber: string
  email: string
  state: string
  city: string
}

export interface CaptchaInfo {
  key: string
  value: string
}

export interface SettingsInfo {
  sourcetableMountpointGroupIds: Array<number>
}

export interface PasswordInfo {
  username: string
  newPassword: string
  oldPassword?: string
  otpCode?: string
}

export interface ForgotInfo {
  username: string
  phoneNumber: string
}

function axiosFetcher(key: string, params: any) {
  return new Promise((resolve, reject) => {
    const token =
      store.getState().auth.token ||
      localStorage.getItem('auth-token') ||
      sessionStorage.getItem('auth-token')
    if (!token && key !== '/captcha') {
      reject('بدون توکن دسترسی')
      return
    }
    Axios.get(key, { params, headers: { token } })
      .then((response) => resolve(response.data))
      .catch((err: AxiosError) => reject(strings.mapAxiosError(err)))
  })
}

function axiosPost<T>(
  cancelToken: CancelToken,
  url: string,
  data: any,
  mutateSwrKey?: string | string[],
  headers?: any,
  method?: 'post' | 'patch',
): Promise<T> {
  return new Promise((resolve, reject) => {
    const token = store.getState().auth.token

    let config
    if (headers) config = { cancelToken, headers: { ...headers, token } }
    else config = { cancelToken, headers: { token } }

    Axios[method ?? 'post'](url, data, config)
      .then((response) => {
        if (mutateSwrKey) {
          let keys: any[]
          if (_.isString(mutateSwrKey)) {
            keys = [mutateSwrKey]
          } else {
            keys = mutateSwrKey
          }
          for (let k of keys) {
            const promises = cache
              .keys()
              .filter((x) => x.startsWith(k))
              .map((x) => mutate(x))
            Promise.all(promises).then(() => resolve(response.data))
          }
        } else {
          resolve(response.data)
        }
      })
      .catch((err: AxiosError) => {
        if (Axios.isCancel(err)) return
        reject(strings.mapAxiosError(err))
      })
  })
}

export enum SubmitState {
  None,
  Submitting,
  Success,
  Error,
}

const postFetcher = <T>(url: string, params?: any, data?: any) =>
  new Promise<T>((resolve, reject) => {
    const token =
      store.getState().auth.token ||
      localStorage.getItem('auth-token') ||
      sessionStorage.getItem('auth-token')
    if (!token && url !== '/captcha') {
      reject('بدون توکن دسترسی')
      return
    }
    Axios.post(url, data, { params, headers: { token } })
      .then((response) => resolve(response.data))
      .catch((err: AxiosError) => reject(strings.mapAxiosError(err)))
  })

export interface Satellite {
  satelliteId: number
  signalType: string
  cnr: number
}
export const useMountpointSatellites = (mountpointName: string | undefined) =>
  useSWR(
    mountpointName ? `${window.location.origin}/api2/live-mountpoint` : null,
    (url) =>
      postFetcher<{
        data: {
          gps: Satellite[] | null
          glonass: Satellite[] | null
          galileo: Satellite[] | null
          beidou: Satellite[] | null
        }
      }>(url, { name: mountpointName }),
    { refreshInterval: 5000 },
  )

export enum SatelliteSystem {
  GPS = 'gps',
  GLO = 'glo',
  GAL = 'gal',
  BDS = 'bds',
}
export interface MountpointSatellitesHistoryQuery {
  bucket: any
  mountpoint: string
  system: SatelliteSystem
  // prn: number
  from: string
  to: string
}
export interface SatelliteCount {
  TimeBucket: string
  SatelliteCountAvg: number
}
export const useMountpointSatellitesCountHistory = (
  query: MountpointSatellitesHistoryQuery | null,
) =>
  useSWR(
    query
      ? [`${window.location.origin}/api2/satellite-count-avg`, query]
      : null,
    (url, query) => postFetcher<SatelliteCount[]>(url, query),
  )

export interface SatelliteSignal {
  SatelliteID: number
  System: SatelliteSystem
  Signal: string
  CnrAvg: number
  TimeBucket: string
}
export const useMountpointSatelliteSignalsHistory = (
  query: MountpointSatellitesHistoryQuery | null,
) =>
  useSWR(
    query ? [`${window.location.origin}/api2/cnr-avg`, query] : null,
    (url, query) => postFetcher<SatelliteSignal[]>(url, query),
  )

export interface SystemLogsQuery {
  After: number
  Count: number
  /**server(mountpoint) name */
  Filter: string
}
export const useSystemLogs = (query: SystemLogsQuery | undefined) =>
  useSWR(query ? ['/logs', query] : null, (url, query) =>
    postFetcher<string>(url, undefined, query),
  )

export type JobRinexVersion = '2.10' | '2.11' | '3.02' | '3.04'
export interface Job {
  name: string
  station: string
  fromHour: number
  hours: number
  rinexVersion: JobRinexVersion
  interval: // | '10hz'
  // | '5hz'
  // | '2hz'
  // | '1hz'
  '1s' | '2s' | '5s' | '10s' | '15s' | '20s' | '30s' | '60s'
  // satellite systems
  gps: boolean
  glonass: boolean
  beidou: boolean
  sbas: boolean
  qzss: boolean
  galileo: boolean
  //
  singleFreq: boolean
  // ionospheric: boolean
}
export const useCreateJob = () => {
  const cancelToken = useCancelToken()
  return (newJob: Job) => {
    return axiosPost(
      cancelToken,
      `${window.location.origin}/raynex/create-job`,
      newJob,
    )
  }
}

export interface UserJob {
  id: number
  name: string
  station: string
  fromHour: number
  hours: number
  downloadToken: string
  /** ready | processing | pending | failed: error message */
  state: string
}
export const useUserJobs = () =>
  useSWR(`${window.location.origin}/raynex/all-user-jobs`, axiosFetcher, {
    refreshInterval: 10_000,
  })

export const downloadUserJob = (userJob: UserJob) => {
  return new Promise<void>((resolve, reject) => {
    const token =
      store.getState().auth.token ||
      localStorage.getItem('auth-token') ||
      sessionStorage.getItem('auth-token')
    if (!token) {
      reject('بدون توکن دسترسی')
      return
    }
    Axios.get(
      `${window.location.origin}/raynex/download/${userJob.downloadToken}`,
      {
        responseType: 'blob',
      },
    )
      .then(({ data }) => {
        const urlBlob = window.URL.createObjectURL(
          new Blob([data], { type: 'application/zip' }),
        )
        const link = document.createElement('a')
        link.href = urlBlob
        link.setAttribute('download', userJob.name)
        document.body.appendChild(link)
        link.click()
        link.remove()

        resolve()
      })
      .catch(() => (err: AxiosError) => reject(strings.mapAxiosError(err)))
  })
}

export interface NmeaLogs {
  id: number
  nmeaLog: string[] | null
}
export interface UserHistoryQuery {
  /**user ID */
  id: number
  /**start date(RFC3339 format) */
  from: string
  /**end date(RFC3339 format) */
  to: string
}
export const useUserNmeaHistory = (query: UserHistoryQuery | null) => {
  const { data, ...rest } = useSWR(
    query ? ['/nmea-history', query] : null,
    axiosFetcher,
  )

  const nmeaLogs: {
    latitude: number
    longitude: number
    fixQuality: FixQuality
  }[] = []

  if (data && (data as NmeaLogs).nmeaLog) {
    for (let nmea of (data as NmeaLogs).nmeaLog!) {
      const nmeaData = nmea.split(',')
      const coordinates = getLatLngFromNmea(nmeaData)

      const fixQuality = parseInt(nmeaData[6])

      if (coordinates) {
        nmeaLogs.push({
          latitude: coordinates[0],
          longitude: coordinates[1],
          fixQuality: FixQuality[fixQuality] ? fixQuality : FixQuality.NotFixed,
        })
      }
    }
  }

  return {
    nmeaLogs: nmeaLogs.length !== 0 ? nmeaLogs : (data as any)?.nmeaLog,
    ...rest,
  }
}

export interface UserConnectionLog {
  ID: number
  UserID: number
  StartTime: string
  EndTime: string | null
  SourceID: number | null
  Mountpoint: string
  Remote: string
  UserAgent: string
}
export const useUserConnectionHistory = (query: UserHistoryQuery | null) => {
  const { data, ...rest } = useSWR(
    query ? [`${window.location.origin}/api2/connection-history`, query] : null,
    axiosFetcher,
  )

  return { data: data as UserConnectionLog[] | null | undefined, ...rest }
}

export function useLogout() {
  const token =
    store.getState().auth.token ||
    localStorage.getItem('auth-token') ||
    sessionStorage.getItem('auth-token')

  return () => Axios.post('/logout', {}, { headers: { token } })
}

export function useConfirmPosition() {
  const cancelToken = useCancelToken()
  return (mountpointId: number) => {
    return axiosPost(
      cancelToken,
      `/clear-mountpoint-alert?id=${mountpointId}`,
      {},
      undefined,
      {},
      'patch',
    )
  }
}
